import React, { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useLead } from '../contexts/LeadContext';
import { MODELS } from '../data/models';
import { PhotoPlaceholder } from './PhotoPlaceholder';
import { Reveal } from './Reveal';
import { trackVehicleInterest, trackViewContent } from '../tracking';
import { MODELS_ID, scrollToLeadForm } from '../utils/scroll';

/**
 * Model interest capture. Picking a card stores the model in shared state, fires
 * VehicleInterest, and pre-fills the form's vehicle field.
 *
 * Full-bleed photography with the type set over it — the photograph is the card, not
 * a thumbnail inside one. No tilt, no shadow, no border: the image is the decoration.
 *
 * The only treatment over a photo is a bottom-anchored scrim, present solely so white
 * type stays legible. No color overlay, duotone, or filter touches the image itself.
 */
export function MostWanted() {
  const {
    t
  } = useLanguage();
  const {
    vehicleInterest,
    setVehicleInterest
  } = useLead();
  const reduce = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const viewed = useRef(false);

  // Meta standard ViewContent: the visitor actually reached the model lineup.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !viewed.current) {
          viewed.current = true;
          trackViewContent('Most Wanted Models');
        }
      });
    }, {
      threshold: 0.25
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const choose = (id: (typeof MODELS)[number]['id'], name: string) => {
    setVehicleInterest(id);
    trackVehicleInterest(name);
    scrollToLeadForm();
  };
  return <section ref={sectionRef} id={MODELS_ID} className="w-full scroll-mt-24 bg-white" data-id="element-345" data-mp="f8p782iz9j12">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 md:py-20" data-id="element-346" data-mp="3s8yi9rpcz2i3">
        <Reveal data-id="element-347" data-mp="3sb1bslppzdrk">
          <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-[-0.03em] text-ink sm:text-[2.75rem]" data-id="element-348" data-mp="3nirs3nf779xq">
            {t('wanted.title')}
          </h2>
          <div aria-hidden="true" className="mt-3 h-[3px] w-12 bg-kred" data-id="element-349" data-mp="3dkc0zkg680o8" />
          <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-body" data-id="element-350" data-mp="3dm57mzqsakw8">{t('wanted.sub')}</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10" data-id="element-351" data-mp="f0c6rgpd1x25">
          <ul className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 md:mx-0 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible md:px-0 md:pb-0" data-id="element-352" data-mp="a4n1qxuknnyg">
            {MODELS.map(model => {
            const selected = vehicleInterest === model.id;
            return <li key={model.id} className="w-[84vw] shrink-0 snap-center sm:w-[62vw] md:w-auto" data-id="element-353" data-mp="2puzs6lor9umg">
                  <motion.button type="button" onClick={() => choose(model.id, model.name)} aria-pressed={selected} whileHover={reduce ? undefined : {
                y: -4
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 26
              }} className="group relative block aspect-[3/4] w-full overflow-hidden bg-ink text-left" data-id="element-354" data-mp="2kvde6a13sacl">
                    {model.photo ? <img src={model.photo} alt={model.name} loading="lazy" decoding="async" className={`h-full w-full object-cover transition-transform duration-700 ease-out ${reduce ? '' : 'group-hover:scale-[1.04]'}`} data-id="element-355" data-mp="1k24ove196plo" /> : <PhotoPlaceholder label={t('wanted.photo', {
                  model: model.name.toUpperCase()
                })} tone="dark" data-id="element-356" data-mp="3uvh98qyl7tik" />}

                    {/* Legibility scrim only — bottom-anchored, no tint across the image. */}
                    <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-ink via-ink/70 to-transparent" data-id="element-357" data-mp="1p5487g6khq8d" />

                    {/* Selected state reads as a red rule along the base, never a fill. */}
                    {selected && <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-kred" data-id="element-358" data-mp="16f4lv1242p8n" />}

                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6" data-id="element-359" data-mp="1k6j7zgrbf6jx">
                      <p className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-gold" data-id="element-360" data-mp="1ffswrmw7k9hb">
                        {t(model.line)}
                      </p>
                      <h3 className="mt-2 font-display text-2xl font-extrabold uppercase leading-none tracking-[-0.02em] text-white sm:text-3xl" data-id="element-361" data-mp="rnlxaev6rmdo">
                        {model.name}
                      </h3>
                      <span className={`mt-4 inline-block text-[11px] font-bold uppercase tracking-[0.16em] ${selected ? 'text-kred' : 'text-white/75'}`} data-id="element-362" data-mp="ib6cxg4az8po">
                        {t('wanted.cta')}
                      </span>
                    </div>
                  </motion.button>
                </li>;
          })}
          </ul>
          <p className="mt-3 text-xs text-muted md:hidden" data-id="element-363" data-mp="118ppuutvtbhd">{t('wanted.swipe')}</p>
        </Reveal>
      </div>
    </section>;
}